import React, { useEffect, useState } from 'react';
import { Button, Table, Modal, Placeholder } from 'react-bootstrap';

import {
  deleteWfh as deleteWfhFromStore,
} from "state/slices/wfh/wfhSlice";
import { useDeleteWfhMutation, useLazyGetWfhRecordingsQuery } from 'state/slices/wfh/wfhApiSlice';

import { useDispatch, useSelector } from "react-redux";

import { showSuccessToast } from "utils/toast";
import { formatDate } from "utils/date";
import { RECORDING_CLIPS_PAGE_SIZE, RECORDING_MODES } from 'constants';

import HRPagination from './HRPagination';

const WFHTable = ({
  wfhs,
  isAllowedToDelete,
  isWFHsLoading,
  refetch,
  showSelfies = false,
}) => {

  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.auth);

  // Pagination states
  const ITEMS_PER_PAGE = 10;
  const totalPages = Math.ceil(wfhs.length / ITEMS_PER_PAGE) || 0;
  const [currentPage, setCurrentPage] = useState(1);

  // Selfie modal state
  const [showSelfieModal, setShowSelfieModal] = useState(false);
  const [selectedSelfie, setSelectedSelfie] = useState(null);
  const [imageError, setImageError] = useState(false);
  const [showClipsModal, setShowClipsModal] = useState(false);
  const [clipRows, setClipRows] = useState([]);
  const [clipsLoading, setClipsLoading] = useState(false);
  const [clipsLoadingMore, setClipsLoadingMore] = useState(false);
  const [clipsHasMore, setClipsHasMore] = useState(false);
  const [clipsWfhId, setClipsWfhId] = useState(null);
  const [clipsStats, setClipsStats] = useState(null);
  const [clipsRecordingMode, setClipsRecordingMode] = useState(RECORDING_MODES.interval);
  const [fetchRecordings] = useLazyGetWfhRecordingsQuery();

  const formatDuration = (ms = 0) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const formatRecordingStatus = (stats, recordingMode) => {
    if (recordingMode === RECORDING_MODES.continuous) {
      const label = stats?.status ? stats.status.replace('_', ' ') : 'not started';
      const coverage = Number.isFinite(stats?.coveragePercent) ? `${stats.coveragePercent}%` : '0%';
      return `${stats?.parts || 0} parts · ${coverage} · ${label}`;
    }
    return `${stats?.taken || 0} / ${stats?.expected || 0}`;
  };

  // Calculate the start and end index for the current page
  const indexOfLastItem = currentPage * ITEMS_PER_PAGE;
  const indexOfFirstItem = indexOfLastItem - ITEMS_PER_PAGE;

  // Remove sorting logic; just use the data as-is
  const pageWfhData = wfhs.slice(indexOfFirstItem, indexOfLastItem);

  // Check if user can view selfies
  const canViewSelfies = showSelfies;

  const canViewClips = Boolean(
    userInfo?.role?.isAdmin // || userInfo?.role?.isTeamWise
  );

  // Delete WFH
  const [deleteWfh] = useDeleteWfhMutation();

  const deleteWfhManage = async (wfhId) => {
    try {
      await deleteWfh(wfhId).unwrap();
      dispatch(deleteWfhFromStore(wfhId));

      showSuccessToast("WFH record deleted successfully");
      refetch();
    } catch (err) {
      // Error toast is automatically shown by baseQuery
    }
  };

  useEffect(() => {
    if ((wfhs.length % ITEMS_PER_PAGE) === 0) {
      setCurrentPage(1);
    }
  }, [wfhs]);

  const handleViewSelfie = (wfhId) => {
    // Reset error state
    setImageError(false);
    // Construct the API URL for the selfie image
    const selfieUrl = `/api/wfhs/selfie/${wfhId}`;
    setSelectedSelfie(selfieUrl);
    setShowSelfieModal(true);
  };

  const handleCloseSelfieModal = () => {
    setShowSelfieModal(false);
    // Delay clearing the state to avoid React errors during unmount
    setTimeout(() => {
      setSelectedSelfie(null);
      setImageError(false);
    }, 300);
  };

  const handleViewClips = async (wfhId) => {
    setShowClipsModal(true);
    setClipsLoading(true);
    setClipRows([]);
    setClipsHasMore(false);
    setClipsWfhId(wfhId);
    setClipsStats(null);
    setClipsRecordingMode(RECORDING_MODES.interval);
    try {
      const data = await fetchRecordings({
        wfhId,
        skip: 0,
        limit: RECORDING_CLIPS_PAGE_SIZE,
      }).unwrap();
      setClipRows(data.recordings || []);
      setClipsHasMore(Boolean(data.hasMore));
      setClipsRecordingMode(data.recordingMode || RECORDING_MODES.interval);
      setClipsStats(data.recordingStats || null);
    } catch (err) {
      setClipRows([]);
      setClipsHasMore(false);
      setClipsStats(null);
    }
    setClipsLoading(false);
  };

  const handleLoadMoreClips = async () => {
    if (!clipsWfhId || clipsLoadingMore || !clipsHasMore) {
      return;
    }
    setClipsLoadingMore(true);
    try {
      const data = await fetchRecordings({
        wfhId: clipsWfhId,
        skip: clipRows.length,
        limit: RECORDING_CLIPS_PAGE_SIZE,
      }).unwrap();
      setClipRows((prev) => [...prev, ...(data.recordings || [])]);
      setClipsHasMore(Boolean(data.hasMore));
    } catch (err) {}
    setClipsLoadingMore(false);
  };

  const handleCloseClipsModal = () => {
    setShowClipsModal(false);
    setTimeout(() => {
      setClipRows([]);
      setClipsHasMore(false);
      setClipsWfhId(null);
      setClipsLoadingMore(false);
      setClipsStats(null);
    }, 300);
  };

  const ClipSkeleton = () => (
    <div className="mb-4">
      <Placeholder as="div" animation="glow" className="mb-2">
        <Placeholder xs={4} />
      </Placeholder>
      <Placeholder as="div" animation="glow">
        <Placeholder
          className="w-100"
          style={{ height: '200px', display: 'block', borderRadius: '4px' }}
        />
      </Placeholder>
    </div>
  );

  const formatClipTime = (isoString) => {
    if (!isoString) return '—';
    try {
      return new Date(isoString).toLocaleString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        day: '2-digit',
        month: 'short',
      });
    } catch (err) {
      return isoString;
    }
  };

  const emptyColSpan = 5 + (canViewClips ? 1 : 0) + (canViewSelfies ? 1 : 0) + (isAllowedToDelete ? 1 : 0);

  return (
    <div>
      <Table striped responsive bordered className="text-center">
        <thead>
          <tr>
            <th>S No.</th>
            <th>Date</th>
            <th>Day</th>
            <th>Punch-In</th>
            <th>Punch-Out</th>
            {canViewClips && <th>Clips</th>}
            {canViewSelfies && <th>Selfie</th>}
            {isAllowedToDelete && <th>Action</th>}
          </tr>
        </thead>
        <tbody>
          {
            pageWfhData.length > 0 ?
              pageWfhData.map((row, index) => (
                <tr key={row.id}>
                  <td>{indexOfFirstItem + index + 1}</td>
                  <td>{formatDate(row.date, 'dd-MM-yyyy', userInfo?.timezone) || row.date}</td>
                  <td>{row.day}</td>
                  <td>{row.timeIn}</td>
                  <td>{row.timeOut ? row.timeOut : '—'}</td>
                  {canViewClips && (
                    <td className="align-middle">
                      {(() => {
                        const stats = row.recordingStats || { expected: 0, taken: 0, parts: 0 };
                        const recordingMode = row.recordingMode || RECORDING_MODES.interval;
                        const viewCount = recordingMode === RECORDING_MODES.continuous
                          ? (stats.parts || stats.taken || 0)
                          : (stats.taken || 0);
                        return (
                          <div className="d-inline-flex align-items-center justify-content-center gap-2">
                            <span className="text-nowrap">{formatRecordingStatus(stats, recordingMode)}</span>
                            {viewCount > 0 && (
                              <Button
                                size="sm"
                                variant="outline-primary"
                                onClick={() => handleViewClips(row.id)}
                                title="View recordings"
                                aria-label="View recordings"
                              >
                                <i className="bi bi-camera-video"></i>
                              </Button>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                  )}
                  {canViewSelfies && (
                    <td>
                      {row.selfieImage ? (
                        <Button
                          size="sm"
                          variant="outline-primary"
                          onClick={() => handleViewSelfie(row.id)}
                          title="View Selfie"
                        >
                          <i className="bi bi-image"></i>
                        </Button>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                  )}
                  {isAllowedToDelete && (
                    <td>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        onClick={() => deleteWfhManage(row.id)}
                      >
                        <i className="bi bi-trash3"></i>
                      </Button>
                    </td>
                  )}
                </tr>
              ))
              :
              (
                <tr className="bg-light border text-center">
                  <td colSpan={emptyColSpan} className="text-muted">
                    {isWFHsLoading ? "Loading Data..." : "No Data Found"}
                  </td>
                </tr>
              )
          }
        </tbody>
      </Table>
      {/* Pagination */}
      <div className="d-flex justify-content-end">
        <HRPagination
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      </div>

      {/* Selfie Modal */}
      <Modal show={showSelfieModal} onHide={handleCloseSelfieModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Punch-In Selfie</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center">
          {selectedSelfie && !imageError && (
            <img
              src={selectedSelfie}
              alt="Punch-in selfie"
              style={{
                maxWidth: '100%',
                maxHeight: '70vh',
                borderRadius: '8px'
              }}
              onError={() => {
                setImageError(true);
              }}
            />
          )}
          {imageError && (
            <div className="alert alert-danger" role="alert">
              <i className="bi bi-exclamation-triangle-fill me-2"></i>
              Failed to load image. The selfie may not be available on the server.
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseSelfieModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showClipsModal} onHide={handleCloseClipsModal} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Screen recordings</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {clipsRecordingMode === RECORDING_MODES.continuous && clipsStats && (
            <div className="mb-3 small text-muted">
              Coverage: {clipsStats.coveragePercent || 0}% · Recorded: {formatDuration(clipsStats.recordedDurationMs)} · Status: {clipsStats.status || 'unknown'}
              {clipsStats.gapDetected ? ' · Gap detected' : ''}
            </div>
          )}
          {clipsLoading && (
            <>
              {Array.from({ length: RECORDING_CLIPS_PAGE_SIZE }).map((_, index) => (
                <ClipSkeleton key={`clip-skeleton-${index}`} />
              ))}
            </>
          )}
          {!clipsLoading && clipRows.length === 0 && (
            <p className="text-muted mb-0">No clips saved for this punch.</p>
          )}
          {!clipsLoading && clipRows.map((clip, index) => (
            <div key={clip.id} className="mb-4">
              <div className="mb-2">
                <strong>
                  {clipsRecordingMode === RECORDING_MODES.continuous
                    ? `Part ${Number.isFinite(clip.partIndex) ? clip.partIndex + 1 : index + 1}`
                    : formatClipTime(clip.recordedAt)}
                </strong>
                {clipsRecordingMode === RECORDING_MODES.continuous && (
                  <span className="text-muted ms-2">{formatClipTime(clip.recordedAt)}</span>
                )}
              </div>
              <video
                controls
                preload="none"
                poster="/video-thumbnail.jpg"
                src={`/api/wfhs/recordings/${clip.id}/file`}
                style={{ width: '100%', maxHeight: '360px', background: '#000' }}
              />
            </div>
          ))}
          {clipsLoadingMore && (
            <>
              {Array.from({ length: RECORDING_CLIPS_PAGE_SIZE }).map((_, index) => (
                <ClipSkeleton key={`clip-more-skeleton-${index}`} />
              ))}
            </>
          )}
          {!clipsLoading && !clipsLoadingMore && clipsHasMore && (
            <div className="text-center">
              <Button
                variant="outline-primary"
                onClick={handleLoadMoreClips}
                disabled={clipsLoadingMore}
              >
                {clipsLoadingMore ? 'Loading...' : 'Load more'}
              </Button>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseClipsModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default WFHTable;
